import { managePageFilters, UrlContextParams, removeAllPageFilters } from '../../../utils/urlUtil'
import {
  SORT_BY,
  END_DATE,
  IS_ALL,
  IS_AT_RISK,
  IS_DRAFT,
  IS_RESOLVED,
  IS_SENSITIVE,
  IS_UNRESOLVED,
  PAGE,
  SORT_DIRECTION,
  START_DATE,
  PAGE_VIEW_KEY,
  SEARCH_QUERY,
  PAGE_VIEW_VALUES,
  FILTER_TYPES_V2,
  FILTER_DATE_CUSTOM_KEY,
  FILTER_TYPES,
  FILTER_ATTRIBUTE_SET
} from '../../../constants'
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import {
  TableFilter,
  TableFilterProps,
  TableFilterList,
  TableFilterType,
  FilterTypes,
  OnFilterChangeProps,
  FilterPaginationProps,
  SearchConfig
} from '@lightbeamai/design-system'
import { Dropdown } from 'semantic-ui-react'
import { FormattedMessage, useIntl } from 'react-intl'

export type FilterValueGroup = { groupId: string; groupName: string; groupDisabled?: boolean }
export type FilterValuesData = {
  key: string
  name: string
  isDisabled?: boolean
  groups?: FilterValueGroup[]
}

export type FilterActionType = {
  id: string
  filterValue: string[]
  action: 'add' | 'remove'
  toggle?: boolean
}

export interface FilterItemProps {
  title: string
  key: string
  keyChild?: string
  titleGroup?: string
  tooltip?: string
  disabledTooltip?: string
  values: string[]
  valuesData?: FilterValuesData[]
  type?: FILTER_TYPES_V2 | FILTER_TYPES
  allowParentSelection?: boolean
  isSorted?: boolean
  filterPaginationProps?: FilterPaginationProps
  searchConfig?: SearchConfig
}
export interface FilterIProps {
  context: UrlContextParams
  pageName: string
  title?: string
  compact?: boolean
  filters: FilterItemProps[]
  sort?: { value?: string; text: string; selected?: boolean }[]
  showSearch?: boolean
  changeFunction?: (key: string, setValue?: string | string[]) => void
  showViewToggle?: boolean
  showAllFiltersOption?: boolean
  pageView?: PAGE_VIEW_VALUES
  onSearch?: (event) => void
  onViewToggle?: (view: string) => void
  filterProps?: Omit<TableFilterProps, 'filters'>
  customFilters?: React.ReactNode
  customSearch?: React.ReactNode
  visibleFiltersCount?: number
}
const TabFilters = [
  PAGE,
  SORT_DIRECTION,
  SORT_BY,
  START_DATE,
  END_DATE,
  `${IS_SENSITIVE}_${PAGE}`, // Todo; this type of page filter name should be generalized
  `${IS_AT_RISK}_${PAGE}`
]
const PageFilters = [
  IS_ALL,
  IS_AT_RISK,
  IS_SENSITIVE,
  IS_DRAFT,
  IS_RESOLVED,
  IS_UNRESOLVED,
  PAGE_VIEW_KEY,
  SEARCH_QUERY
]

const TableFilters = (props: FilterIProps): React.ReactElement => {
  const intl = useIntl()
  const {
    filters,
    context,
    pageName: filterPage,
    showSearch,
    pageView,
    showViewToggle,
    onSearch,
    onViewToggle,
    changeFunction,
    sort = [],
    filterProps,
    customFilters,
    customSearch,
    visibleFiltersCount,
    showAllFiltersOption
  } = props
  const [filtersArray, setFiltersArray] = useState<TableFilterList>([])
  const childKeysByParentKey = useMemo(() => {
    const parentKeys = {}

    for (const filter of filters) {
      parentKeys[filter.key] = filter.keyChild
    }

    return parentKeys
  }, [filters])

  // Helper function to generate filter options from valuesData
  const generateFilterOptionsFromValuesData = (valuesData) => {
    return valuesData.map((value) => ({
      key: value.key,
      label: value.name
    }))
  }

  // Helper function to generate grouped filter options

  const generateGroupedFilterOptions = (
    valuesData,
    generateUniqueKeys = false,
    disabledTooltip = ''
  ) => {
    const groupedOptions: any[] = []

    valuesData?.forEach((valueData) => {
      if (valueData.groups && valueData.groups.length > 0) {
        valueData.groups.forEach((group) => {
          let groupObject = groupedOptions.find((g) => g.key === group.groupId)

          if (!groupObject) {
            groupObject = {
              key: group.groupId,
              label: group.groupName,
              disabled: true, // Initialize with true; will update later based on children
              options: [],
              disabledTooltip
            }
            groupedOptions.push(groupObject)
          }

          // Determine if the child is disabled based on the group data
          const childDisabled = group.groupDisabled

          groupObject.options.push({
            // If generateUniqueKeys is true, create a unique key using the format childKey_parentKey
            key: generateUniqueKeys ? `${valueData.key}___${group.groupId}` : valueData.key,
            label: valueData.name,
            disabled: childDisabled
          })

          // If at least one option is not disabled, the group should not be disabled
          if (!childDisabled) {
            groupObject.disabled = false
          }
        })
      } else if (valueData?.key && valueData?.name) {
        groupedOptions.push({
          key: valueData.key,
          label: valueData.name
        })
      }
    })

    return groupedOptions
  }

  // Function to map data filters to AllFilterProps
  const convertFilterData = (filters): TableFilterList => {
    return filters.map((filter) => {
      const {
        title,
        key,
        values,
        valuesData,
        type,
        disabledTooltip,
        filterPaginationProps,
        searchConfig
      } = filter

      let filterOptions: any[] = []
      if (key === FILTER_ATTRIBUTE_SET) {
        filterOptions = generateGroupedFilterOptions(valuesData, true, disabledTooltip)
      } else if (type === 'grouped' && valuesData && valuesData.length) {
        filterOptions = generateGroupedFilterOptions(valuesData)
      } else if (type === FILTER_TYPES_V2.dateRange) {
        filterOptions = valuesData.map((value) => ({
          key: value.key,
          label: value.name
        }))
      } else if (valuesData && valuesData.length) {
        filterOptions = generateFilterOptionsFromValuesData(valuesData)
      } else if (values && values.length) {
        filterOptions = values.map((value) => ({
          key: value,
          label: value
        }))
      }

      let filterType = FilterTypes.MULTI_SELECT
      if (type === FILTER_TYPES_V2.dateRange) {
        filterType = FilterTypes.DATE_RANGE
      } else if (type === FILTER_TYPES_V2.toggle) {
        filterType = FilterTypes.SINGLE_SELECT
      }

      return {
        id: key,
        // check if filter is a intl id and if so use the intl id as the label else use the title
        filterLabel: intl.messages[title] || title,
        filterOptions,
        search: true,
        filterType,
        filterPaginationProps,
        searchConfig
      }
    })
  }

  // Update filtersArray when filters prop changes
  useEffect(() => {
    if (filters.length > 0) {
      const filterData = convertFilterData(filters)
      setFiltersArray(filterData)
    } else {
      setFiltersArray([])
    }
  }, [filters])

  const filtersToControl = Object.entries(context.filters)
    .filter(([key]) => ![...TabFilters, ...PageFilters].includes(key))
    .map(([key, value]) => {
      return Array.isArray(value) ? value.map((val) => ({ key, value: val })) : { key, value }
    })
    .flat()

  // Update the filtersArray when context changes
  useEffect(() => {
    if (filtersToControl.length > 0) {
      setFiltersArray((prev) => {
        return prev.map((filter) => {
          const filterKey = filter.id

          // Check if filterKey has a child key
          const childKey = childKeysByParentKey[filterKey]

          const filterValues = filtersToControl.filter((f) => f.key === filterKey && f.value !== '')
          const filterValuesArray = filterValues.map((f) => f.value)

          // Check filterKey is attributeSet[] as we need it's child key as well
          if (filterKey === FILTER_ATTRIBUTE_SET && childKey) {
            const childFilterValues = filtersToControl.filter(
              (f) => f.key === childKey && f.value !== ''
            )
            const childFilterValuesArray = childFilterValues.map((f) => f.value)

            // Find groupData of child from filter props
            const allChildGroupData = filters.find((f) => f.key === filterKey)?.valuesData
            const filteredChildGroupData = allChildGroupData?.filter((f) =>
              childFilterValuesArray.includes(f.key)
            )

            // Flat map the filtered child data for all groups to get the selected values in the format of key___groupId
            const selectedGroupValues =
              filteredChildGroupData?.flatMap((f) => {
                if (f?.groups?.length && f.groups.length > 0) {
                  return f?.groups?.map((g) => `${f.key}___${g.groupId}`)?.filter((g) => g)
                }

                return []
              }) || []

            // return { ...filter, value: [...filterValuesArray, ...childFilterValuesArray] } as TableFilterType
            return {
              ...filter,
              value: [...filterValuesArray, ...selectedGroupValues]
            } as TableFilterType
          }

          // TODO: Handle child filters value based on childKeysByParentKey

          if (
            filter.filterType === FilterTypes.MULTI_SELECT ||
            filter.filterType === FilterTypes.SINGLE_SELECT
          ) {
            return { ...filter, value: filterValuesArray } as TableFilterType
          }

          if (filter.filterType === FilterTypes.DATE_RANGE) {
            const firstFilter =
              filterValues && filterValues.length > 0 ? filterValues[0] : undefined
            const dateRangeRawValue = firstFilter?.value
            if (typeof dateRangeRawValue === 'string') {
              const dateRangeValue = dateRangeRawValue.split(',')
              if (dateRangeValue.length === 1) {
                return { ...filter, value: dateRangeValue[0] } as TableFilterType
              } else if (dateRangeValue.length === 3) {
                return {
                  ...filter,
                  value: dateRangeValue[0],
                  dateRange: [dateRangeValue[1], dateRangeValue[2]]
                } as TableFilterType
              }
            }
          }

          return filter
        })
      })
    }
  }, [context])

  const generateFilterActions = (values: OnFilterChangeProps[], filtersToControl) => {
    const actions: FilterActionType[] = []

    values.forEach((valueObj) => {
      const hasChildKey = !!childKeysByParentKey[valueObj.id]
      const childKey: string | undefined = childKeysByParentKey[valueObj.id]

      // Used to determine current applied filters
      const correspondingFilters = filtersToControl
        .filter((filter) => filter.key === valueObj.id)
        .map((filter) => filter.value)

      let correspondingChildFilters: string[] = []

      // Used to determine current applied child filters
      if (hasChildKey) {
        correspondingChildFilters = filtersToControl
          .filter((filter) => filter.key === childKey)
          .map((filter) => filter.value)
      }

      // Check for added filters (in selectedFilters but not in filtersToControl)

      // Check for added filters (in selectedFilters but not in filtersToControl)
      if (
        valueObj.filterType === FilterTypes.MULTI_SELECT &&
        valueObj.id === FILTER_ATTRIBUTE_SET
      ) {
        // Handle parentKey
        const parentKeys = valueObj.selectedFilters.filter(
          (filter) => !(filter as string).includes('___')
        )
        const parentKeyToAdd = parentKeys.filter((filter) => !correspondingFilters.includes(filter))
        const parentKeyToRemove = correspondingFilters.filter(
          (filter) => !parentKeys.includes(filter)
        )

        if (parentKeyToAdd.length > 0) {
          actions.push({
            id: valueObj.id,
            filterValue: parentKeyToAdd as string[],
            action: 'add'
          })
        }

        if (parentKeyToRemove.length > 0) {
          actions.push({
            id: valueObj.id,
            filterValue: parentKeyToRemove as string[],
            action: 'remove',
            toggle: false
          })
        }

        // Handle childKey
        if (childKey) {
          // Get original child keys
          const childKeys = valueObj.selectedFilters
            .filter((filter) => (filter as string).includes('___'))
            .map((filter) => {
              const parts = (filter as string).split('___')
              return parts.length > 0 ? parts[0] : undefined
            })

          const childKeyToAdd = childKeys.filter(
            (filter) => filter && !correspondingChildFilters.includes(filter)
          )
          const childKeyToRemove = correspondingChildFilters.filter(
            (filter) => filter && !childKeys.includes(filter)
          )

          if (childKeyToAdd.length > 0) {
            actions.push({
              id: childKey,
              filterValue: childKeyToAdd as string[],
              action: 'add'
            })
          }

          if (childKeyToRemove.length > 0) {
            actions.push({
              id: childKey,
              filterValue: childKeyToRemove as string[],
              action: 'remove'
            })
          }
        }
      } else if (
        valueObj.filterType === FilterTypes.MULTI_SELECT ||
        valueObj.filterType === FilterTypes.SINGLE_SELECT
      ) {
        const addedFilters = valueObj.selectedFilters.filter(
          (selected) => !correspondingFilters.includes(selected)
        )
        if (addedFilters.length > 0) {
          actions.push({
            id: valueObj.id,
            filterValue: addedFilters as string[],
            action: 'add',
            toggle: valueObj.filterType === FilterTypes.SINGLE_SELECT ? true : false
          })
        }

        // Check for removed filters (in filtersToControl but not in selectedFilters)
        const removedFilters = correspondingFilters.filter(
          (filter) => !valueObj.selectedFilters.includes(filter)
        )
        if (removedFilters.length > 0) {
          actions.push({
            id: valueObj.id,
            filterValue: removedFilters,
            action: 'remove',
            toggle: valueObj.filterType === FilterTypes.SINGLE_SELECT ? true : false
          })
        }
      } else if (valueObj.filterType === FilterTypes.DATE_RANGE) {
        if (valueObj) {
          // Remove filter if value is empty
          if (!valueObj.value) {
            actions.push({
              id: valueObj.id,
              filterValue: [],
              action: 'remove'
            })
          }
          // Add filter if value is 'custom_date_range' and dateRange is not empty
          else if (
            valueObj.value === FILTER_DATE_CUSTOM_KEY &&
            valueObj.dateRange &&
            valueObj.dateRange.length === 2
          ) {
            actions.push({
              id: valueObj.id,
              filterValue: [valueObj.value, valueObj.dateRange[0], valueObj.dateRange[1]],
              action: 'add'
            })
          }
          // Add filter if value is not empty
          else if (valueObj.value) {
            actions.push({
              id: valueObj.id,
              filterValue: [valueObj.value],
              action: 'add'
            })
          }
        }
      }
    })

    return actions
  }

  const applyFilter = (values: OnFilterChangeProps[]) => {
    if (values.length === 0) {
      removeAllPageFilters(filterPage)
      return
    }

    // handle changeFunction for singleSelect filters and multiSelect filters
    if (changeFunction) {
      values.forEach((valueObj) => {
        if (valueObj.filterType === FilterTypes.SINGLE_SELECT) {
          const selectedFilter = valueObj.selectedFilters[0]
          changeFunction(valueObj.id, selectedFilter as string)
        } else if (valueObj.filterType === FilterTypes.MULTI_SELECT) {
          changeFunction(valueObj.id, valueObj.selectedFilters as string[])
        }
      })
    }

    const actions = generateFilterActions(values, filtersToControl)

    // If the filter is a single select filter
    // Remove 'remove' action from the actions array when there are two actions in actions array (add and remove)
    if (
      values.length === 1 &&
      values[0].filterType === FilterTypes.SINGLE_SELECT &&
      actions.length == 2
    ) {
      const singleSelectAction = actions.find((action) => action.action === 'remove')
      if (singleSelectAction) {
        actions.splice(actions.indexOf(singleSelectAction), 1)
      }
    }

    actions.map((action) => {
      managePageFilters({
        pageName: filterPage,
        filterKey: action.id,
        filterValues: action.filterValue,
        action: action.action,
        toggle: action.toggle ? action.toggle : false
      })
    })
  }

  const setSort = (_: SyntheticEvent, data) => {
    setSelectedSortValue(data.value)
    managePageFilters({
      pageName: filterPage,
      filterKey: SORT_BY,
      filterValues: [data.value],
      action: data.value ? 'add' : 'remove'
    })
  }

  const defaultSortValue = sort.find((selected) => !!selected)?.value
  const [selectedSortValue, setSelectedSortValue] = useState(context.pageSort || defaultSortValue)
  return (
    <>
      <TableFilter
        filters={filtersArray}
        onChange={(values) => {
          applyFilter(values)
        }}
        savedFilters={[]}
        showSearch={showSearch}
        onSearch={onSearch}
        onViewToggle={(view) => onViewToggle && onViewToggle(view)}
        currentView={pageView}
        showViewToggle={showViewToggle}
        customSearch={customSearch}
        visibleFiltersCount={visibleFiltersCount}
        showAllFiltersOption={showAllFiltersOption}
        {...filterProps}
        customSortBy={
          sort.length ? (
            <div className="sort-container">
              <div className="sort-label">
                <label htmlFor="sort">
                  <FormattedMessage id="label.text.sort" />
                </label>
              </div>
              <div className="xs-mr-12 xs-ml-12 sort-select">
                <Dropdown
                  placeholder={intl.formatMessage({
                    id: 'sort.dropdown.placeholder'
                  })}
                  clearable
                  selection
                  defaultValue={selectedSortValue}
                  options={sort}
                  onChange={setSort}
                />
              </div>
            </div>
          ) : (
            <>{customFilters && customFilters}</>
          )
        }
      />
    </>
  )
}

export default TableFilters
